import React from 'react'
import { Drawer, Box, Typography, IconButton, Divider, CircularProgress, Chip, Grid } from '@mui/material'
import { IconSeeker, CardSeeker } from '@ui-seeker/core'
import {
    userAnalysisOpen,
    selectedUserForAnalysis,
    userAnalysisData,
    userActivityData,
    isLoadingUserAnalysis
} from '../@signals'
import DailyChart from './DailyChart'

export default function UserAnalysisDrawer() {
    const handleClose = () => {
        userAnalysisOpen.value = false
        selectedUserForAnalysis.value = null
    }

    const data = userAnalysisData.value
    const activity = userActivityData.value

    return (
        <Drawer
            anchor="right"
            open={userAnalysisOpen.value}
            onClose={handleClose}
            PaperProps={{
                sx: { width: { xs: '100%', sm: 450 } }
            }}
        >
            <Box sx={{ p: 2 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">User Analysis</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <IconSeeker icon="xmark" />
                    </IconButton>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {isLoadingUserAnalysis.value ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : data ? (
                    <Box>
                        {/* User info */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {data.user_email || `User ${data.user_id}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Last {data.days_period} days activity
                            </Typography>
                        </Box>

                        {/* Stats cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6}>
                                <CardSeeker sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="h4" color="primary.main">
                                        {data.total_mappings?.toLocaleString() || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Mappings
                                    </Typography>
                                </CardSeeker>
                            </Grid>
                            <Grid item xs={6}>
                                <CardSeeker sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="h4" color="success.main">
                                        {data.daily_average || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Daily Average
                                    </Typography>
                                </CardSeeker>
                            </Grid>
                        </Grid>

                        {/* By Action */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>By Action Type</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {data.by_action && Object.entries(data.by_action).map(([action, count]) => (
                                    <Chip
                                        key={action}
                                        label={`${action}: ${count}`}
                                        size="small"
                                        color={action === 'MAP' ? 'success' : action === 'UNMAP' ? 'error' : 'warning'}
                                    />
                                ))}
                                {(!data.by_action || Object.keys(data.by_action).length === 0) && (
                                    <Typography variant="body2" color="text.secondary">No data</Typography>
                                )}
                            </Box>
                        </Box>

                        {/* By Source */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>By Source</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {data.by_source && Object.entries(data.by_source).map(([source, count]) => (
                                    <Chip
                                        key={source}
                                        label={`${source}: ${count}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                ))}
                                {(!data.by_source || Object.keys(data.by_source).length === 0) && (
                                    <Typography variant="body2" color="text.secondary">No data</Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Daily Activity Chart */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Daily Activity</Typography>
                            <DailyChart data={activity || []} />
                        </Box>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Select a user to view their analysis
                    </Typography>
                )}
            </Box>
        </Drawer>
    )
}
